import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2, Bookmark, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchBlogs, fetchBlog, fetchAuthor } from '../lib/api';
import Seo from '../components/Seo';
import { buildBlogSchema } from '../lib/schema';
import NotFound from './NotFound';

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    fetchBlog(id)
      .then((b) => {
        setBlog(b);
        if (b.author_id) fetchAuthor(b.author_id).then(setAuthor).catch(() => {});
        fetchBlogs().then((all) => setRelated(all.filter((x) => x.id !== b.id).slice(0, 3))).catch(() => {});
      })
      .catch(() => setBlog(null));
  }, [id]);

  if (blog === undefined) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  }
  if (blog === null) return <NotFound />;

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title={blog.meta_title || `${blog.title} | Learndunia`}
        description={blog.meta_description || blog.excerpt}
        keywords={blog.meta_keywords}
        image={blog.og_image || blog.image}
        canonical={blog.canonical_url || `https://learndunia.com/blog/${blog.slug || blog.id}`}
        jsonLd={buildBlogSchema(blog, author)}
      />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-teal-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>

        <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-teal-200 mb-3">{blog.category}</Badge>
        <h1 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{blog.title}</h1>

        <div className="flex items-center flex-wrap gap-4 text-sm text-slate-500 mt-5">
          {author ? (
            <Link to={`/author/${author.slug}`} className="inline-flex items-center gap-2 hover:text-teal-700">
              <img src={author.avatar} alt={author.name} className="w-7 h-7 rounded-full object-cover" />
              <span className="font-semibold text-slate-700">{author.name}</span>
              {author.is_fact_checker && <ShieldCheck className="w-3.5 h-3.5 text-teal-600" />}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {blog.author}</span>
          )}
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {blog.date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {blog.readTime}</span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm"><Bookmark className="w-4 h-4 mr-1" /> Save</Button>
            <Button variant="outline" size="sm"><Share2 className="w-4 h-4 mr-1" /> Share</Button>
          </div>
        </div>

        <img src={blog.image} alt={blog.title} className="w-full aspect-[16/9] object-cover rounded-2xl mt-8" />

        {blog.excerpt && <p className="text-lg text-slate-700 leading-relaxed mt-8"><em>{blog.excerpt}</em></p>}

        {blog.content ? (
          <article className="prose prose-slate max-w-none mt-8" dangerouslySetInnerHTML={{ __html: blog.content }} />
        ) : (
          <article className="prose prose-slate max-w-none mt-8">
            <p>The full article content will appear here once published.</p>
          </article>
        )}

        {author && (
          <div className="mt-10 p-5 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
            <img src={author.avatar} alt={author.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
            <div className="min-w-0">
              <Link to={`/author/${author.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{author.name}</Link>
              {author.designation && <div className="text-xs text-slate-500">{author.designation}</div>}
              {author.bio && <div className="text-sm text-slate-700 mt-1 line-clamp-3" dangerouslySetInnerHTML={{ __html: author.bio }} />}
            </div>
          </div>
        )}
      </div>

      <div className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h3 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Related articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug || b.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">{b.category}</span>
                  <h4 className="mt-3 font-semibold text-slate-900 leading-snug group-hover:text-teal-700 line-clamp-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{b.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
